/**
 * A refused availability save (400 invalid_value) in plain words.
 *
 * The note and the mentions' dates are the fields a person types. A
 * refused date on a client mentioned is theirs. A refusal of the note is theirs
 * only when the note sent was over the cap: the sentence then states the
 * cap. Every other refusal (the week, the stance, the hours, the on-leave
 * flag, the version, or a note under the cap) is a value Olive sent, so
 * the sentence says nothing the person typed caused it, and the screen
 * reports it to the Errors tab with the page and the field, never the
 * note's text.
 */
import { ApiError } from '@/lib/api';
import { mintReference, reportPageCrash } from '@/lib/appErrors';
import { AVAILABILITY_NOTES_MAX } from '@/lib/capacity';
import { fieldOf } from '@/lib/errorCopy';
import { fieldSentence } from '@/lib/fieldErrorCopy';

export interface AvailabilityRefusal {
  sentence: string;
  /** True when the refused value is one Olive sent, not one typed. */
  appCaused: boolean;
  /** The refused field as the server named it; 'body' when it named none. */
  field: string;
}

/** The error code an app-caused refusal is reported under. */
export const APP_REFUSED_VALUE = 'app_refused_value';

/** The refusal, or null when the failure is not a 400. `sentNotes` is the
 *  note the save sent (null or absent when it sent none). */
export function availabilityRefusal(err: unknown, sentNotes?: string | null): AvailabilityRefusal | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const field = fieldOf(err) ?? 'body';
  const overCap = typeof sentNotes === 'string' && sentNotes.length > AVAILABILITY_NOTES_MAX;
  if (field === 'notes' && overCap) {
    return { sentence: fieldSentence('the note', { kind: 'max_length', max: AVAILABILITY_NOTES_MAX }), appCaused: false, field };
  }
  // A client mentioned with a date the record refuses (a death before the
  // birth): the dates are typed, so the person's to fix.
  if (field === 'mentions') {
    return { sentence: "One of the dates for a client you mentioned isn't allowed, so nothing was saved. Make sure each date is today or earlier, then save again.", appCaused: false, field };
  }
  return { sentence: fieldSentence("this week's availability", { kind: 'app_fault' }), appCaused: true, field };
}

/** Tells the office an app-caused refusal happened: the page (the
 *  router's path) and the field only. Fire and forget. */
export function reportAppRefusal(tenantSlug: string, pagePath: string, field: string): void {
  reportPageCrash(tenantSlug, {
    reference: mintReference(),
    route: pagePath,
    error_code: APP_REFUSED_VALUE,
    detail: `availability save refused the field ${field}`,
  });
}
